import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getClientInfo, ClientInfo } from "../../api/Client";

interface ClientController {
  client: ClientInfo | null;
  loading: boolean;
  error: string | null;
  refreshClientInfo: () => Promise<void>;
}

/**
 * Hook for managing client data and state
 */
export const useClientController = (): ClientController => {
  const {
    data: client,
    isLoading: loading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: ["clientInfo"],
    queryFn: async () => {
      const response = await getClientInfo();

      if (response.success && response.data) {
        if (Array.isArray(response.data)) {
          return {
            members: response.data,
            clientId:
              response.data.length > 0 ? response.data[0].clientId : undefined,
          } as ClientInfo;
        } else {
          return {
            id: response.data.id,
            name: response.data.name,
            members: response.data.members,
            clientId: response.data.clientId,
          } as ClientInfo;
        }
      } else {
        throw new Error(response.error || "Failed to fetch client information");
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });

  const error = queryError ? (queryError as Error).message : null;

  const refreshClientInfo = async () => {
    await refetch();
  };

  return {
    client: client || null,
    loading,
    error,
    refreshClientInfo,
  };
};
